import React, { useRef, useState } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import MovieEditIcon from '@mui/icons-material/MovieFilter';
import CloseIcon from '@mui/icons-material/Close';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import { RectangularImageCropperModal } from '../ImageCropper/RectangularImageCropperModal';
import { OBSIDIAN, OBSIDIAN_BODY_FONT, OBSIDIAN_DISPLAY_FONT } from '../../constants/obsidianDashboardTheme';
import { showToastMessage } from '../../utils/AppUtils';

type Props = {
    previewUrl: string;
    onCropped: (previewBase64: string, file: File) => void;
    onClear: () => void;
};

export function SandboxCoverImagePicker({ previewUrl, onCropped, onClear }: Props) {
    const inputRef = useRef<HTMLInputElement>(null);
    const [cropSrc, setCropSrc] = useState('');
    const [cropOpen, setCropOpen] = useState(false);

    const openPicker = () => inputRef.current?.click();

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        if (!file.type.startsWith('image/')) {
            showToastMessage('Please choose an image file', 'error');
            return;
        }
        if (file.size > 8 * 1024 * 1024) {
            showToastMessage('Image must be under 8MB', 'info');
            return;
        }
        setCropSrc(URL.createObjectURL(file));
        setCropOpen(true);
    };

    const revokeCropSrc = () => {
        if (cropSrc.startsWith('blob:')) URL.revokeObjectURL(cropSrc);
        setCropSrc('');
    };

    const handleCropSave = (base64: string, file: File) => {
        setCropOpen(false);
        revokeCropSrc();
        onCropped(base64, file);
    };

    const handleCropCancel = () => {
        setCropOpen(false);
        revokeCropSrc();
    };

    return (
        <Box>
            <input ref={inputRef} type="file" accept="image/*" hidden onChange={handleFileChange} />
            <Box
                onClick={openPicker}
                sx={{
                    position: 'relative',
                    p: 1.5,
                    borderRadius: 3,
                    bgcolor: OBSIDIAN.surfaceContainer,
                    cursor: 'pointer',
                    overflow: 'hidden',
                }}>
                {previewUrl ? (
                    <Box
                        sx={{
                            position: 'relative',
                            width: '100%',
                            aspectRatio: '16 / 9',
                            borderRadius: 2,
                            overflow: 'hidden',
                            bgcolor: OBSIDIAN.surfaceContainerLow,
                        }}>
                        <Box
                            component="img"
                            src={previewUrl}
                            alt="Course cover"
                            sx={{
                                position: 'absolute',
                                inset: 0,
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                display: 'block',
                            }}
                        />
                        <IconButton
                            size="small"
                            onClick={e => {
                                e.stopPropagation();
                                onClear();
                            }}
                            sx={{
                                position: 'absolute',
                                top: 8,
                                right: 8,
                                bgcolor: 'rgba(0,0,0,0.65)',
                                color: '#fff',
                                zIndex: 1,
                                '&:hover': { bgcolor: 'rgba(0,0,0,0.85)' },
                            }}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                        <Box
                            sx={{
                                position: 'absolute',
                                bottom: 8,
                                right: 8,
                                zIndex: 1,
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.75,
                                px: 1.25,
                                py: 0.5,
                                borderRadius: 999,
                                bgcolor: 'rgba(0,0,0,0.7)',
                                color: OBSIDIAN.primary,
                                fontSize: 11,
                                fontWeight: 700,
                                fontFamily: OBSIDIAN_BODY_FONT,
                            }}>
                            <PhotoCameraIcon sx={{ fontSize: 14 }} />
                            Change & crop
                        </Box>
                    </Box>
                ) : (
                    <Box
                        sx={{
                            width: '100%',
                            aspectRatio: '16 / 9',
                            borderRadius: 2,
                            bgcolor: OBSIDIAN.surfaceContainerLow,
                            textAlign: 'center',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            px: 2,
                        }}>
                        <Box
                            sx={{
                                width: 48,
                                height: 48,
                                borderRadius: '50%',
                                bgcolor: OBSIDIAN.surfaceContainerHighest,
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: OBSIDIAN.primary,
                                mb: 1,
                            }}>
                            <MovieEditIcon sx={{ fontSize: 22 }} />
                        </Box>
                        <Typography
                            sx={{
                                fontFamily: OBSIDIAN_DISPLAY_FONT,
                                fontWeight: 600,
                                fontSize: 12,
                                color: OBSIDIAN.onSurface,
                                mb: 0.5,
                            }}>
                            16:9 keyframe art or trailer still
                        </Typography>
                        <Typography
                            sx={{ fontFamily: OBSIDIAN_BODY_FONT, fontSize: 11, color: OBSIDIAN.onSurfaceVariant }}>
                            Select an image, then crop to 16:9. Uploads when you save the course.
                        </Typography>
                    </Box>
                )}
            </Box>

            <RectangularImageCropperModal
                open={cropOpen}
                src={cropSrc}
                onSave={handleCropSave}
                onCancel={handleCropCancel}
                aspect={16 / 9}
                cropHeight={280}
                title="Crop Course Cover"
            />
        </Box>
    );
}
